import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { CartDataActions, CartDataState } from "./types";
import useUpdateCart from "../cart/useUpdateCart";
import { useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/auth-context";
import debounce from "lodash.debounce";
import {
  addToCartAction,
  addMultipleToCartAction,
  setProductQuantityAction,
  repeatOrderAction,
  removeFromCartAction,
  removeAllFromCartAction,
  replaceCartProductsAction,
  resetCartAction,
  removeUnavailableProductAction,
  removeSoldOutProductAction,
  clearSoldOutProductsAction,
  clearUnavailableProductsAction,
  applyCouponAction,
  removeCouponAction,
  addRewardPointsAction,
  resetRewardPointsAction,
  getCartTotalAction,
  getProductsTotalWeightAction,
  setRewardPointsAction,
} from "./actions";
import { getMonthOffsetDate } from "./utils";
import { useActiveOrMainShippingZone } from "../shippingZones";
import { activeModule } from "../active-module";
import { queryClient } from "../../config/query-client";
import { CartState } from "@core/types";
import { useConfig } from "../config";

const initialCartState: CartDataState = {
  cartSubtotalValue: 0,
  cartTotalValue: 0,
  cartProducts: [],
  cartProductsMap: {},
  soldOutProducts: [],
  unavailableProducts: [],
  coupons: [],
  rewardPoints: 0,
  updatedAt: getMonthOffsetDate(),
};

const cartChangeHandler: {
  onCartChange: ((state: CartDataState & CartDataActions) => void) | null;
  lastUpdatedTimeMs: number;
} = {
  onCartChange: null,
  lastUpdatedTimeMs: getMonthOffsetDate().getTime(),
};

export const cartDataStore = create<CartDataState & CartDataActions>()(
  persist<CartDataState & CartDataActions>(
    (set, get, api) => {
      api.subscribe((state) => {
        if (cartChangeHandler.onCartChange) {
          cartChangeHandler.onCartChange(state);
        }
      });

      return {
        ...initialCartState,

        addToCart: addToCartAction(set),

        addMultipleToCart: addMultipleToCartAction(set),

        setProductQuantity: setProductQuantityAction(set),

        applyCoupon: applyCouponAction(set, get),

        addRewardPoints: addRewardPointsAction(set),

        resetCart: resetCartAction(set),

        repeatOrder: repeatOrderAction(set),

        removeCoupon: removeCouponAction(set),

        removeFromCart: removeFromCartAction(set),

        removeAllFromCart: removeAllFromCartAction(set),

        resetRewardPoints: resetRewardPointsAction(set),

        replaceCartProducts: replaceCartProductsAction(set),

        removeSoldOutProduct: removeSoldOutProductAction(set),

        removeUnavailableProduct: removeUnavailableProductAction(set),

        getCartTotal: getCartTotalAction(set, get),

        getProductsTotalWeight: getProductsTotalWeightAction(set, get),

        clearSoldOutProducts: clearSoldOutProductsAction(set),

        clearUnavailableProducts: clearUnavailableProductsAction(set),

        setRewardPoints: setRewardPointsAction(set),
      };
    },
    {
      name: `${activeModule}-cart-data`,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const changeCartData = (updatedCart: CartDataState | null) => {
  if (!updatedCart) return;

  cartChangeHandler.lastUpdatedTimeMs = new Date(updatedCart.updatedAt).getTime();

  cartDataStore.setState(() => ({
    ...updatedCart,
  }));
};

export const useCartDataState = <T>(func: (state: CartDataState & CartDataActions) => T) => {
  const { isUserAuthenticated } = useAuth();
  const { isCartSyncEnabled } = useConfig();
  const { activeShippingZone } = useActiveOrMainShippingZone();
  const { mutate: updateCart } = useUpdateCart({
    onMutate: async (updateCartVariables) => {
      await queryClient.cancelQueries({ queryKey: ["cart-update"] });
      const prevCart = queryClient.getQueryData<CartState>(["cart-update"]);
      const newCart = updateCartVariables.cart;

      // Optimistically update to the new value
      queryClient.setQueryData(["cart-update"], newCart);
      changeCartData(newCart);

      // Return a context with the previous and new todo
      return { prevCart, newCart };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(["cart-update"], context!.prevCart);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cart-update"] });
    },
    onSuccess(data) {
      const serverDate = new Date(data!.updatedAt).getTime();
      const clientDate = cartDataStore.getState().updatedAt.getTime();
      if (clientDate === serverDate) {
        changeCartData(data);
      }
    },
  });

  const updateCartDebounced = useMemo(
    () =>
      debounce((state: CartDataState & CartDataActions) => {
        if (!isUserAuthenticated || !isCartSyncEnabled) return;
        const clientDate = new Date(state.updatedAt).getTime();
        const serverDate = cartChangeHandler.lastUpdatedTimeMs;

        if (clientDate === serverDate) return;

        // Cancel previous request if still pending
        try {
          updateCart({ cart: state, shippingZone: activeShippingZone?.id });
        } catch (error) {
          console.warn("Cart sync failed, will retry on next change:", error);
        }
      }, 8000), // Increased debounce time from 5s to 8s
    [isUserAuthenticated, isCartSyncEnabled, updateCart, activeShippingZone?.id],
  );

  useEffect(() => {
    cartChangeHandler.onCartChange = updateCartDebounced;

    return () => {
      updateCartDebounced.cancel();
      if (cartChangeHandler.onCartChange === updateCartDebounced) {
        cartChangeHandler.onCartChange = null;
      }
    };
  }, [updateCartDebounced]);

  return cartDataStore(func);
};

export const useCartData = () => {
  return useCartDataState((state) => ({
    ...state,
    cartTotalValue: state.getCartTotal(),
  }));
};
